import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { type ConnectionTestResult } from '../../services/connectionTest';
import { storage } from '@doxyme/ui/utils';
import { StorageKeys } from '../../shared/constants';

export enum ConnectionTestResultAdditionalForTracking {
  NotCompleted = 'not_completed_call_started', // if test was not completed because the call started
  MediaError = 'media_error', // if test wasn't done due to cam/mic disabling
  Error = 'error', // something went wrong (test doesn't work because of some reason)
}

export type ConnectionTestResultForTracking =
  | ConnectionTestResult
  | ConnectionTestResultAdditionalForTracking;

export type TrackingSlice = {
  initialMicAccess: boolean;
  initialCameraAccess: boolean;
  connectionTestResult: ConnectionTestResultForTracking | null;

  /**
   * Do not track a call end event if the call start event was not tracked.
   * e.g. if user closes the tab abruptly before the session is initialized.
   * @see useTrackCallStartEvent
   * @see useTrackCallEndEvent
   */
  hasTrackedCallStart: boolean;

  /**
   * When a call end event is tracked, remember not to track it duplicate,
   * e.g. if user hangs up then closes the tab (which emits unload event).
   * @see useTrackCallEndEvent
   */
  hasTrackedCallEnd: boolean;
  pinCount: number;
  unpinCount: number;
  userFlipCamera: boolean;
  isHidePreview: boolean;
};

const initialState: TrackingSlice = {
  initialMicAccess: false,
  initialCameraAccess: false,
  connectionTestResult: null,
  hasTrackedCallStart: false,
  hasTrackedCallEnd: false,
  pinCount: 0,
  unpinCount: 0,
  userFlipCamera: false,
  isHidePreview:
    storage.getItem(StorageKeys.LocalPreviewExpanded, true) ?? false,
};

export const trackingSlice = createSlice({
  name: 'tracking',
  initialState,
  reducers: {
    setInitialMicAccess: (state, action: PayloadAction<boolean>) => {
      return { ...state, initialMicAccess: action.payload };
    },
    setInitialCameraAccess: (state, action: PayloadAction<boolean>) => {
      return { ...state, initialCameraAccess: action.payload };
    },
    setConnectionTestResult: (
      state,
      action: PayloadAction<ConnectionTestResultForTracking>,
    ) => {
      return { ...state, connectionTestResult: action.payload };
    },
    setTrackedCallStart(state) {
      state.hasTrackedCallStart = true;
      state.hasTrackedCallEnd = false;
    },
    setTrackedCallEnd(state) {
      state.hasTrackedCallEnd = true;
    },
    incrementPinCount(state) {
      state.pinCount = state.pinCount + 1;
    },
    incrementUnpinCount(state) {
      state.unpinCount = state.unpinCount + 1;
    },
    resetPinAndUnpinCount(state) {
      state.pinCount = 0;
      state.unpinCount = 0;
    },
    setUserFlipCamera(state) {
      state.userFlipCamera = true;
    },
    setIsHidePreview(state, action: PayloadAction<boolean>) {
      state.isHidePreview = action.payload;
    },
  },
});

export const {
  setInitialCameraAccess,
  setInitialMicAccess,
  setConnectionTestResult,
  setTrackedCallStart,
  setTrackedCallEnd,
  incrementPinCount,
  incrementUnpinCount,
  resetPinAndUnpinCount,
  setUserFlipCamera,
  setIsHidePreview,
} = trackingSlice.actions;
